import { useCallback, useMemo, useRef, useState } from "react";
import { Badge, Button, Input, PageHeader, Section } from "@lumika/ui";
import { useApiChannel, type ChannelStatus } from "./useApiChannel";
import { TOOL_EVENT_TYPES, type ClientToolEvent, type RealtimeEvent } from "./types";
import { ChannelTokenPicker } from "./ChannelTokenPicker";
import { WS_URL } from "./config";

interface Bubble {
  id: string;
  role: "user" | "assistant";
  text: string;
  streaming?: boolean;
}

interface ToolChip {
  id: string;
  type: string;
  label: string;
  payload: unknown;
}

interface ErrorChip {
  id: string;
  code?: number | string;
  message: string;
}

type ConversationItem =
  | { kind: "bubble"; value: Bubble }
  | { kind: "tool"; value: ToolChip }
  | { kind: "error"; value: ErrorChip };

const STATUS_VARIANT: Record<
  ChannelStatus["kind"],
  "secondary" | "warning" | "success" | "destructive"
> = {
  disconnected: "secondary",
  connecting: "warning",
  connected: "success",
  error: "destructive",
};

let __chipCounter = 0;
const nextChipId = (): string => `chip-${++__chipCounter}`;

export function ChannelsPlaygroundApiPage() {
  const [apiToken, setApiToken] = useState("");
  const [input, setInput] = useState("");
  const [conversation, setConversation] = useState<ConversationItem[]>([]);
  const [rawEvents, setRawEvents] = useState<RealtimeEvent[]>([]);

  // messageId -> bubble id. A streaming assistant turn is one bubble we
  // append deltas into; the map keeps that wiring stable even if the
  // server interleaves turns.
  const streamingRef = useRef<Map<string, string>>(new Map());

  const handleEvent = useCallback((event: RealtimeEvent) => {
    setRawEvents((prev) => [...prev, event]);

    switch (event.type) {
      case "message_update": {
        const existingBubbleId = streamingRef.current.get(event.messageId);
        if (existingBubbleId) {
          setConversation((prev) =>
            prev.map((item) =>
              item.kind === "bubble" && item.value.id === existingBubbleId
                ? {
                    ...item,
                    value: {
                      ...item.value,
                      text: item.value.text + event.delta,
                    },
                  }
                : item,
            ),
          );
        } else {
          const newId = `bubble-${event.messageId}`;
          streamingRef.current.set(event.messageId, newId);
          setConversation((prev) => [
            ...prev,
            {
              kind: "bubble",
              value: {
                id: newId,
                role: "assistant",
                text: event.delta,
                streaming: true,
              },
            },
          ]);
        }
        return;
      }
      case "message_end": {
        const bubbleId = streamingRef.current.get(event.messageId);
        if (bubbleId) {
          streamingRef.current.delete(event.messageId);
          setConversation((prev) =>
            prev.map((item) =>
              item.kind === "bubble" && item.value.id === bubbleId
                ? {
                    ...item,
                    value: {
                      ...item.value,
                      text: event.text,
                      streaming: false,
                    },
                  }
                : item,
            ),
          );
        } else if (event.role === "assistant") {
          // Non-streamed assistant reply (server emitted no deltas).
          setConversation((prev) => [
            ...prev,
            {
              kind: "bubble",
              value: {
                id: `bubble-${event.messageId}`,
                role: "assistant",
                text: event.text,
              },
            },
          ]);
        }
        // role === "user" echoes are skipped; we already pushed an
        // optimistic bubble on send and don't want a duplicate.
        return;
      }
      case "error": {
        setConversation((prev) => [
          ...prev,
          {
            kind: "error",
            value: {
              id: nextChipId(),
              code: event.code,
              message: event.message,
            },
          },
        ]);
        return;
      }
      default: {
        if (isToolEvent(event)) {
          setConversation((prev) => [
            ...prev,
            {
              kind: "tool",
              value: {
                id: nextChipId(),
                type: event.type,
                label: event.label,
                payload: event.payload,
              },
            },
          ]);
        }
      }
    }
  }, []);

  const channel = useApiChannel(handleEvent);

  // No auto-connect: the user supplies a token (typed or picked from a
  // dealership channel) and clicks Connect. Connect doubles as reset — it
  // clears the transcript and (re)opens the socket. StrictMode's
  // double-invoke is harmless; the hook closes any prior socket first.
  const onConnect = useCallback(() => {
    const token = apiToken.trim();
    if (!token) return;
    setConversation([]);
    setRawEvents([]);
    streamingRef.current.clear();
    channel.connect({ wsUrl: WS_URL, token });
  }, [channel, apiToken]);

  const onSend = useCallback(() => {
    const text = input.trim();
    if (!text || channel.status.kind !== "connected") return;
    setConversation((prev) => [
      ...prev,
      {
        kind: "bubble",
        value: { id: `local-${Date.now()}`, role: "user", text },
      },
    ]);
    channel.send(text);
    setInput("");
  }, [channel, input]);

  const statusLabel = useMemo(() => {
    switch (channel.status.kind) {
      case "disconnected":
        return "disconnected";
      case "connecting":
        return "connecting…";
      case "connected":
        return "connected";
      case "error":
        return `error${
          channel.status.code ? ` (${channel.status.code})` : ""
        }: ${channel.status.reason}`;
    }
  }, [channel.status]);

  const connected = channel.status.kind === "connected";

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <PageHeader
        eyebrow="API Channel"
        title="Drive the deployed API channel"
        description={
          <>
            Pick a dealership's API channel (or paste a token), then connect to{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
              {WS_URL}
            </code>{" "}
            over WebSocket and watch streaming events arrive in the raw log.
          </>
        }
      />

      <Section title="Channel">
        <div className="flex flex-col gap-4">
          <ChannelTokenPicker
            channelType="api"
            token={apiToken}
            onTokenChange={setApiToken}
            tokenLabel="API integration token"
          />
          <div>
            <Button onClick={onConnect} disabled={!apiToken.trim()}>
              {connected ? "Reconnect" : "Connect"}
            </Button>
          </div>
        </div>
      </Section>

      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        <Badge variant={STATUS_VARIANT[channel.status.kind]}>{statusLabel}</Badge>
        <span>
          sessionId{" "}
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
            {channel.sessionId ?? "—"}
          </code>
        </span>
        <span>
          leadId{" "}
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
            {channel.leadId ?? "—"}
          </code>
        </span>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="flex min-h-[20rem] flex-col rounded-lg border bg-card">
          <header className="border-b px-4 py-3">
            <h2 className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Conversation
            </h2>
          </header>
          <div className="max-h-[60vh] flex-1 overflow-y-auto p-4">
            {conversation.length === 0 ? (
              <p className="text-sm italic text-muted-foreground">
                No messages yet. Send something to start.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {conversation.map((item) => {
                  if (item.kind === "bubble") {
                    const isUser = item.value.role === "user";
                    return (
                      <li
                        key={item.value.id}
                        className={[
                          "max-w-[90%] whitespace-pre-wrap break-words rounded-xl px-3.5 py-2.5 text-sm leading-relaxed",
                          isUser
                            ? "self-end bg-primary text-primary-foreground"
                            : "self-start bg-muted text-foreground",
                        ].join(" ")}
                      >
                        {item.value.text || (item.value.streaming ? "…" : "")}
                        {item.value.streaming && (
                          <span className="ml-0.5 animate-pulse">▌</span>
                        )}
                      </li>
                    );
                  }
                  if (item.kind === "tool") {
                    return (
                      <li key={item.value.id} className="self-stretch text-xs">
                        <details className="overflow-hidden rounded-lg border border-amber-300 bg-amber-50 text-amber-800">
                          <summary className="cursor-pointer list-none px-2.5 py-1.5 [&::-webkit-details-marker]:hidden">
                            <span className="mr-2 font-mono font-semibold">
                              {item.value.type}
                            </span>
                            <span className="text-amber-700">
                              {item.value.label}
                            </span>
                          </summary>
                          <pre className="overflow-x-auto border-t border-amber-300 bg-card p-2.5 text-[11px] text-foreground">
                            {JSON.stringify(item.value.payload, null, 2)}
                          </pre>
                        </details>
                      </li>
                    );
                  }
                  return (
                    <li
                      key={item.value.id}
                      className="self-stretch rounded-lg bg-destructive/10 px-2.5 py-1.5 text-xs text-destructive"
                    >
                      error
                      {item.value.code !== undefined
                        ? ` (${item.value.code})`
                        : ""}
                      : {item.value.message}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        <section className="flex min-h-[20rem] flex-col rounded-lg border bg-muted/40">
          <header className="flex items-center justify-between border-b px-4 py-3">
            <h2 className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
              Raw events
            </h2>
            <Button variant="outline" size="sm" onClick={() => setRawEvents([])}>
              Clear
            </Button>
          </header>
          <div className="max-h-[60vh] flex-1 overflow-y-auto p-4">
            <ul className="flex flex-col gap-2">
              {rawEvents.map((event, i) => (
                <li key={i}>
                  <pre className="overflow-x-auto rounded-md border bg-card p-2 font-mono text-[11px]">
                    {JSON.stringify(event, null, 2)}
                  </pre>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          onSend();
        }}
      >
        <Input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            connected
              ? "type a message and press Enter"
              : "connect to send"
          }
          disabled={!connected}
        />
        <Button type="submit" disabled={!connected || !input.trim()}>
          Send
        </Button>
      </form>
    </div>
  );
}

function isToolEvent(event: RealtimeEvent): event is ClientToolEvent {
  return TOOL_EVENT_TYPES.has(event.type);
}
